import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n/LanguageContext';
import { Lang, PublicService } from '../../types';
import { PublicApi } from '../../utils/apiClient';
import { fallbackServices } from '../../data/fallback';
import FadeIn from './FadeIn';

/** How many services the home page shows when items are flagged featured. */
export const HOME_SERVICES = 4;

const HINT: Record<Lang, string> = {
  de: 'Fotos ansehen', en: 'View photos', it: 'Guarda le foto',
};

export const ServiceCard: React.FC<{ s: PublicService; i: number; onOpen: (imgs: string[]) => void }> = ({ s, i, onOpen }) => {
  const { lang } = useI18n();
  const clickable = (s.gallery?.length || 0) > 0;
  return (
    <FadeIn
      className="svc-card"
      style={{ transitionDelay: `${(i % 4) * 0.05}s`, cursor: clickable ? 'pointer' : undefined }}
      onClick={clickable ? () => onOpen(s.gallery) : undefined}
      title={clickable ? HINT[lang] : undefined}
    >
      <h3 className="svc-title">{s.title}</h3>
      <p className="svc-desc">{s.desc}</p>
      {s.capacities?.length > 0 && (
        <div className="cap-grid">
          {s.capacities.map((c, ci) => (
            <div className="cap-item" key={ci}>
              <span className="cap-num">{c.num}</span>
              <span className="cap-lbl">{c.label}</span>
            </div>
          ))}
        </div>
      )}
    </FadeIn>
  );
};

const Services: React.FC<{ onOpenGallery: (images: string[]) => void }> = ({ onOpenGallery }) => {
  const { t, lang } = useI18n();
  const [items, setItems] = useState<PublicService[]>(() => fallbackServices(lang));

  useEffect(() => {
    let cancelled = false;
    setItems(fallbackServices(lang));
    PublicApi.services(lang)
      .then((d) => { if (!cancelled && d && d.length) setItems(d); })
      .catch(() => { if (!cancelled) setItems(fallbackServices(lang)); });
    return () => { cancelled = true; };
  }, [lang]);

  // Home shows featured services; if none flagged (bundled/prerender), show the first N.
  const featured = items.filter((s) => s.featured);
  const home = featured.length ? featured.slice(0, HOME_SERVICES) : items.slice(0, HOME_SERVICES);

  return (
    <section id="services">
      <div className="container">
        <FadeIn>
          <span className="slabel sl-y">{t('svc.badge')}</span>
          <h2 className="stitle">{t('svc.title')}</h2>
          <p className="ssub">{t('svc.sub')}</p>
        </FadeIn>
        <div className="services-grid">
          {home.map((s, i) => <ServiceCard key={s.id} s={s} i={i} onOpen={onOpenGallery} />)}
        </div>
        {items.length > 0 && (
          <div className="catalog-cta">
            <Link to="/servizi" className="btn btn-p">{t('svc.viewAll')}</Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default Services;
